import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useCarouselSlides, getStrapiMediaUrl } from '../../../lib/strapi';
import { ResponsiveImage } from '../../../lib/rescale';
import { OptimizedImage } from '../../../components/ui/OptimizedImage';

// Importar iconos SVG
import gatosIcon from '/images/svg/gatos-icon.svg';
import perrosIcon from '/images/svg/perros-icon.svg';
import roedoresIcon from '/images/svg/roedores-icon.svg';
import avesIcon from '/images/svg/aves-icon.svg';
// Slides de respaldo por si falla la carga desde Strapi
const fallbackSlides = [
  {
    id: 'slide1',
    title: 'Nutrición Premium',
    Title: 'Nutrición Premium',
    subtitle: 'Formulado por expertos para una vida saludable',
    buttonText: 'Descubrir alimentos',
    buttonLink: '/products?category=alimentos',
    image: '/hero/premium-food.jpg',
  },
  {
    id: 'slide2',
    title: 'Para cada etapa',
    Title: 'Para cada etapa',
    subtitle: 'Desde cachorros hasta senior, cubrimos todas las necesidades',
    buttonText: 'Ver por edad',
    buttonLink: '/products?category=edades',
    image: '/hero/life-stages.jpg',
  },
  {
    id: 'slide3',
    title: 'Ingredientes naturales',
    Title: 'Ingredientes naturales',
    subtitle: 'Sin conservantes artificiales ni saborizantes',
    buttonText: 'Conocer más',
    buttonLink: '/about',
    image: '/hero/natural-ingredients.jpg',
  }
];

const Hero = () => {
  // Obtener datos de las diapositivas desde Strapi
  const { slides: strapiSlides, loading, error } = useCarouselSlides();
  
  // Estado para controlar el carrusel
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [progress, setProgress] = useState(0);
  const [direction, setDirection] = useState(0);
  const [, setPrevSlideIndex] = useState(0);
  
  // Referencias para los intervalos
  const intervalRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const progressDuration = 6000; // duración de cada slide en ms
  const progressStep = 16; // actualizar el progreso cada 16ms (60fps)
  
  // Determinar qué slides usar (desde Strapi o los de respaldo)
  const displaySlides = (!loading && !error && strapiSlides && strapiSlides.length > 0) 
    ? strapiSlides.sort((a, b) => (a.order || 0) - (b.order || 0))
    : fallbackSlides;
  
  // Manejar la navegación de slides
  const goToSlide = (index: number) => {
    // No cambiar slide si solo hay uno
    if (displaySlides.length <= 1) return;
    
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    if (progressIntervalRef.current) window.clearInterval(progressIntervalRef.current);
    
    // Reiniciar el progreso
    setProgress(0);
    
    // Determinar la dirección del cambio de slide
    setDirection(index > currentSlide ? 1 : -1);
    setPrevSlideIndex(currentSlide);
    setCurrentSlide(index);
    
    if (isAutoplay) startAutoplay();
  };
  
  const nextSlide = () => {
    if (displaySlides.length <= 1) return;
    goToSlide((currentSlide + 1) % displaySlides.length);
  };
  
  const prevSlide = () => {
    if (displaySlides.length <= 1) return;
    goToSlide((currentSlide - 1 + displaySlides.length) % displaySlides.length);
  };
  
  // Iniciar reproducción automática
  const startAutoplay = () => {
    // No iniciar autoplay si solo hay un slide
    if (displaySlides.length <= 1) return;
    
    // Detener intervalos existentes para evitar duplicados
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    if (progressIntervalRef.current) window.clearInterval(progressIntervalRef.current);
    
    // Intervalo para cambiar de slide
    intervalRef.current = window.setInterval(() => {
      const nextIndex = (currentSlide + 1) % displaySlides.length;
      setDirection(1); // Siempre avanzando en autoplay
      setPrevSlideIndex(currentSlide);
      setCurrentSlide(nextIndex);
      setProgress(0);
    }, progressDuration);
    
    // Intervalo para actualizar la barra de progreso
    let startTime = Date.now();
    progressIntervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min(100, (elapsed / progressDuration) * 100);
      setProgress(newProgress);
      
      if (newProgress >= 100) {
        startTime = Date.now();
      }
    }, progressStep);
  };
  
  // Detener reproducción automática
  const stopAutoplay = () => {
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    if (progressIntervalRef.current) window.clearInterval(progressIntervalRef.current);
  };
  
  // Cambiar entre reproducción automática y manual
  const toggleAutoplay = () => {
    // No permitir toggle si solo hay un slide
    if (displaySlides.length <= 1) return;
    
    if (isAutoplay) {
      stopAutoplay();
    } else {
      startAutoplay();
    }
    setIsAutoplay(!isAutoplay);
  };
  
  // Iniciar/detener la reproducción automática en montaje/desmontaje
  useEffect(() => {
    // Solo iniciar autoplay si hay más de un slide
    if (isAutoplay && displaySlides.length > 1) {
      startAutoplay();
    } else {
      stopAutoplay();
    }
    
    return () => {
      stopAutoplay();
    };
  }, [isAutoplay, displaySlides.length]);
  
  // Actualizar cuando cambien los slides de Strapi
  useEffect(() => {
    if (!loading && strapiSlides && strapiSlides.length > 0) {
      // Reiniciar a la primera diapositiva cuando cambian los datos
      setCurrentSlide(0);
      setProgress(0);
      
      // Solo reiniciar autoplay si hay más de un slide
      if (isAutoplay && strapiSlides.length > 1) {
        stopAutoplay();
        startAutoplay();
      } else {
        stopAutoplay();
      }
    }
  }, [strapiSlides, loading]);
  
  // Variantes para animaciones
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.4 }
      }
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.4 }
      }
    })
  };
  
  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.2,
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    })
  };
  
  // Si está cargando, mostrar un skeleton atractivo
  if (loading) {
    return (
      <div className="relative w-full h-screen overflow-hidden bg-gradient-to-br from-black to-gray-900">
        <div className="absolute inset-0 bg-grid-white/5 bg-grid-8"></div>
        
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full max-w-6xl px-6">
            {/* Skeleton para el título */}
            <div className="w-3/4 md:w-2/3 lg:w-1/2 h-16 mb-6 rounded-lg bg-white/10 animate-pulse"></div>
            
            {/* Skeleton para el subtítulo */}
            <div className="w-5/6 md:w-3/4 lg:w-2/3 h-8 mb-12 rounded-lg bg-white/10 animate-pulse"></div>
            
            {/* Skeleton para el botón */}
            <div className="w-48 h-12 rounded-full bg-white/10 animate-pulse"></div>
            
            {/* Logo de carga animado */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <motion.div
                className="w-12 h-12 border-2 border-white/40 rounded-full"
                animate={{
                  borderColor: ['rgba(255,255,255,0.1)', 'rgba(255,255,255,0.9)', 'rgba(255,255,255,0.1)'],
                  rotate: 360
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Obtener información del slide actual
  const slide = displaySlides[currentSlide];
  const slideTitle = slide.Title || slide.title || "";
  const slideSubtitle = slide.subtitle || "";
  const slideButtonText = slide.buttonText || "Ver más";
  const slideButtonLink = slide.buttonLink || "/";
  
  // Obtener URL de la imagen/video (manejar tanto los slides de Strapi como los de respaldo)
  const getMediaUrl = () => {
    if (!slide) return { url: '', type: 'image' };
    
    let url = '';
    let mediaObject = null;
    
    if (typeof slide.image === 'string') {
      url = slide.image;
    } else if (Array.isArray(slide.image)) {
      // Si la imagen es un array (formato Strapi), tomar el primer elemento
      if (slide.image.length > 0) {
        mediaObject = slide.image[0];
        url = getStrapiMediaUrl(slide.image[0]);
      }
    } else if (slide.image) {
      // Si la imagen es un objeto directo
      mediaObject = slide.image;
      url = getStrapiMediaUrl(slide.image);
    }
    
    // Detectar si es video por extensión o mime type
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const isVideoByExtension = videoExtensions.some(ext => url.toLowerCase().includes(ext));
    
    // También verificar si el objeto tiene mime type de video
    const isVideoByMime = mediaObject && mediaObject.mime && mediaObject.mime.startsWith('video/');
    
    const isVideo = isVideoByExtension || isVideoByMime;
    
    return { url, type: isVideo ? 'video' : 'image' };
  };
  
  const { url: mediaUrl, type: mediaType } = getMediaUrl();
  
  // Determinar si mostrar controles (solo si hay más de un slide)
  const showControls = displaySlides.length > 1;
  
  return (
    <div 
      className="relative w-full h-screen overflow-hidden bg-black"
    >
      {/* Overlay degradado para mejor legibilidad del texto */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10" />
      
      {/* Slides */}
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={`slide-${currentSlide}`}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0"
        >
          {/* Imagen o Video de fondo */}
          <div className="absolute inset-0 z-0">
            {mediaUrl ? (
              mediaType === 'video' ? (
                <video 
                  src={mediaUrl}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  Tu navegador no soporta video HTML5.
                </video>
              ) : (
                <ResponsiveImage 
                  src={mediaUrl} 
                  alt={slideTitle} 
                  preset="large"
                  className="w-full h-full object-cover"
                />
              )
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-900 to-gray-700"></div>
            )}
          </div>
          
          {/* Contenido del slide - Posicionado abajo a la izquierda */}
          <div className="relative z-20 h-full flex items-end">
            <div className="container mx-auto px-6 lg:px-10 pb-24">
              <div className="max-w-xl">
                {/* Iconos SVG antes del título */}
                <motion.div 
                  custom={0}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  className="flex space-x-4 mb-6"
                >
                  <div className="w-12 h-12 text-white opacity-90 hover:opacity-100 transition-opacity">
                    <OptimizedImage src={avesIcon} alt="Aves" width={48} height={48} className="w-full h-full invert" lazy={false} />
                  </div>
                  <div className="w-12 h-12 text-white opacity-90 hover:opacity-100 transition-opacity">
                    <OptimizedImage src={roedoresIcon} alt="Roedores" width={48} height={48} className="w-full h-full invert" lazy={false} />
                  </div>
                  <div className="w-12 h-12 text-white opacity-90 hover:opacity-100 transition-opacity">
                    <OptimizedImage src={perrosIcon} alt="Perros" width={48} height={48} className="w-full h-full invert" lazy={false} />
                  </div>
                  <div className="w-12 h-12 text-white opacity-90 hover:opacity-100 transition-opacity">
                    <OptimizedImage src={gatosIcon} alt="Gatos" width={48} height={48} className="w-full h-full invert" lazy={false} />
                  </div>


                </motion.div>

                <motion.h1 
                  custom={1}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  className="text-4xl md:text-6xl font-extrabold text-white mb-4 tracking-tight"
                >
                  {slideTitle}
                </motion.h1>
                
                <motion.p 
                  custom={2}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                  className="text-xl md:text-2xl text-white/90 mb-8 font-light"
                >
                  {slideSubtitle}
                </motion.p>
                
                <motion.div 
                  custom={3}
                  variants={textVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Link 
                    to={slideButtonLink}
                    className="inline-flex items-center px-6 py-3 text-base font-medium transition-all duration-300 rounded-full border-2"
                    style={{
                      backgroundColor: '#f6cf02',
                      color: '#0d0d0d',
                      borderColor: '#0d0d0d'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#0d0d0d';
                      e.currentTarget.style.color = '#f6cf02';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f6cf02';
                      e.currentTarget.style.color = '#0d0d0d';
                    }}
                  >
                    {slideButtonText}
                    <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 -mr-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      
      {/* Controles inferiores - Solo si hay más de un slide */}
      {showControls && (
        <div className="absolute bottom-8 left-0 right-0 z-30 flex flex-col items-center">
          {/* Barra de progreso */}
          <div className="w-1/3 max-w-md h-1 bg-white/20 rounded-full overflow-hidden mb-6">
            <motion.div 
              className="h-full bg-white"
              style={{ width: `${progress}%` }}
              transition={{ ease: "linear" }}
            />
          </div>
          
          <div className="flex items-center justify-center">
            {/* Botón anterior */}
            <button 
              onClick={prevSlide} 
              className="p-2 rounded-full text-white/70 hover:text-white focus:outline-none mx-2"
              aria-label="Slide anterior"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Indicadores de slide */}
            <div className="flex space-x-2 mx-4">
              {displaySlides.map((_, index) => (
                <button
                  key={`indicator-${index}`}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none ${
                    index === currentSlide
                      ? 'bg-white scale-110'
                      : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`Ir a slide ${index + 1}`}
                  aria-current={index === currentSlide ? 'true' : 'false'}
                />
              ))}
            </div>
            
            {/* Botón siguiente */}
            <button 
              onClick={nextSlide} 
              className="p-2 rounded-full text-white/70 hover:text-white focus:outline-none mx-2"
              aria-label="Siguiente slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            {/* Botón autoplay */}
            <button 
              onClick={toggleAutoplay} 
              className="p-2 rounded-full text-white/70 hover:text-white focus:outline-none ml-4"
              aria-label={isAutoplay ? 'Pausar presentación' : 'Reproducir presentación'}
            >
              {isAutoplay ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Indicador de desplazamiento */}
      <motion.div 
        className="absolute bottom-32 right-10 z-30 hidden lg:flex flex-col items-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.8 }}
      >
        <span className="text-white/70 text-sm mb-2 rotate-90 origin-center">Scroll</span>
        <motion.div 
          className="w-0.5 h-10 bg-white/40"
          initial={{ height: 0 }}
          animate={{ height: 40 }}
          transition={{ 
            delay: 2.2, 
            duration: 1,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </motion.div>
    </div>
  );
};

export default Hero; 